import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Alert, Row } from 'react-bootstrap';
import { useAuth } from '../components/autentication-context.component';
import { TransactionStatus } from '../../enum/transaction-status.enum';
import { getPaymentHistory } from '../../services/payment-history.service';
import { DetailedTransaction } from '../interfaces/transaction-details.interface';
import { useNavigate } from 'react-router-dom';
import { SpinnerComponent } from '../components/spinner.component';
import PaginationComponent from '../components/pagination.component';
import TransactionCard from '../components/transaction-card.component';
import { LogoutProps } from '../interfaces/logout.interface';
import { handleApiError } from '../../services/error.service';

const LIMIT = 10;

const PaymentHistory: React.FC<LogoutProps> = ({ handleLogout }) => {
  const { employeeId, token, updateToken } = useAuth();
  const navigate = useNavigate();
  const [paymentHistory, setPaymentHistory] = useState<DetailedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const fetchPaymentHistory = useCallback(async () => {
    if (!employeeId || !token) return;
    
    setLoading(true);
    try {
      const response = await getPaymentHistory(employeeId, LIMIT, offset, token, updateToken);

      if (!response || !Array.isArray(response.transactions)) {
        setNotification({ type: 'error', message: 'Failed to fetch payment history.' });
        setPaymentHistory([]);
        return;
      }

      setTotal(response.total);

      setPaymentHistory(
        response.transactions.map((entry: DetailedTransaction) => ({
          ...entry,
          status: entry.status as TransactionStatus
        }))
      );
    } catch (error) {
      handleApiError({ error, handleLogout, setNotification });
    } finally {
      setLoading(false);
    }
  }, [employeeId, token, offset, updateToken, handleLogout]);

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  const handleCardClick = useCallback(
    (transaction: DetailedTransaction) => navigate('/transaction-details', { state: transaction }),
    [navigate]
  );

  const handlePageChange = useCallback((newOffset: number) => setOffset(newOffset), []);

  return (
    <Container className="mt-5">
      <h3 className="mb-4 greenColour">Total {total}</h3>

      {loading && <SpinnerComponent />}

      {!loading && notification?.type === 'error' && (
        <Alert variant="danger" className="text-center">{notification.message}</Alert>
      )}

      {!loading && paymentHistory.length > 0 && (
        <>
          <Row className="g-4">
            {paymentHistory.map((entry, index) => (
              <TransactionCard key={entry.transaction || index} transaction={entry} onClick={() => handleCardClick(entry)} />
            ))}
          </Row>

          <PaginationComponent total={total} limit={LIMIT} offset={offset} onPageChange={handlePageChange} />
        </>
      )}
    </Container>
  );
};

export default PaymentHistory;